import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  AppShell,
  Accordion,
  Burger,
  Group,
  Button,
  UnstyledButton,
  ThemeIcon,
  Box,
  Text,
  rem,
  Stack,
  useMantineTheme,
  rgba,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconHome,
  IconCalculator,
  IconPackage,
  IconLogout,
  IconGift,
  IconReceipt2,
  IconTicket,
  IconBuildingStore,
  IconCrown,
  IconUserSearch,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';

const MainLink = ({ icon: Icon, color, label, to, active, nested = false }) => {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <UnstyledButton
        sx={(theme) => ({
          display: 'block',
          width: '100%',
          padding: nested
            ? `${theme.spacing.xs} ${theme.spacing.sm}`
            : `${theme.spacing.sm} ${theme.spacing.md}`,
          borderRadius: theme.radius.lg,
          border: `1px solid ${active ? rgba(theme.colors[color][5], 0.24) : 'rgba(255,255,255,0)'}`,
          color: active ? theme.colors.dark[8] : theme.colors.gray[7],
          background: active
            ? `linear-gradient(135deg, ${rgba(theme.colors[color][0], 0.96)}, rgba(255,255,255,0.98))`
            : 'transparent',
          boxShadow: active ? '0 12px 24px rgba(15, 23, 42, 0.06)' : 'none',
          transition: 'transform 160ms ease, background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease',
          '&:hover': {
            backgroundColor: active ? rgba(theme.colors[color][1], 1) : theme.colors.gray[0],
            transform: 'translateX(2px)',
          },
        })}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon
            color={color} 
            variant={active ? 'filled' : 'light'}
            size="lg"
            radius="md"
          >
            <Icon style={{ width: '1.2rem', height: '1.2rem' }} />
          </ThemeIcon>
            <Text size="sm" fw={active ? 600 : 500}>{label}</Text>
          </Group>
          {active && (
            <Box
              sx={(theme) => ({
                width: rem(6),
                height: rem(6),
                borderRadius: 999,
                backgroundColor: theme.colors[color][6],
                flex: '0 0 auto',
              })}
            />
          )}
        </Group>
      </UnstyledButton>
    </Link>
  );
};

const NavigationGroupControl = ({ icon: Icon, color, label, active }) => (
  <Group gap="sm" wrap="nowrap">
    <ThemeIcon
      color={color}
      variant={active ? 'filled' : 'light'}
      size="lg"
      radius="md"
    >
      <Icon style={{ width: '1rem', height: '1rem' }} />
    </ThemeIcon>
    <Text size="sm" fw={active ? 700 : 600} c={active ? 'dark.8' : 'gray.8'}>
      {label}
    </Text>
  </Group>
);

const Layout = () => {
  const { currentUser, logout, isAdmin } = useAuth();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const navigate = useNavigate();
  const [opened, { toggle }] = useDisclosure(false);
  const location = useLocation();
  const [gruposAbiertos, setGruposAbiertos] = useState(['giftcards', 'tienda', 'administracion']);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const navigationGroups = [
    {
      value: 'giftcards', label: 'Giftcards', icon: IconGift, color: 'yellow',
      links: [
        { icon: IconGift, label: 'Giftcards', to: '/gift-cards', color: 'yellow' },
        { icon: IconGift, label: 'Solicitudes giftcards', to: '/giftcard-requests', color: 'orange' },
        { icon: IconReceipt2, label: 'Cupones giftcards', to: '/giftcard-coupons', color: 'yellow' },
        { icon: IconTicket, label: 'Códigos de convenio', to: '/codigos-convenio', color: 'teal' },
      ],
    },
    {
      value: 'tienda', label: 'Ventas de la página', icon: IconBuildingStore, color: 'teal',
      links: [
        { icon: IconUserSearch, label: 'Solicitudes a domicilio', to: '/solicitudes-a-domicilio', color: 'orange' },
        { icon: IconPackage, label: 'Pedidos tienda', to: '/product-orders', color: 'blue' },
        ...(isAdmin ? [
          { icon: IconBuildingStore, label: 'Catálogo tienda', to: '/store-products', color: 'teal' },
        ] : []),
        { icon: IconCrown, label: 'Premium Club', to: '/premium-club', color: 'red' },
      ],
    },
    {
      value: 'administracion', label: 'Administración', icon: IconReceipt2, color: 'violet',
      links: isAdmin ? [
        { icon: IconReceipt2, label: 'Deudas Club', to: '/deudas-club', color: 'red' },
        { icon: IconUserSearch, label: 'Postulaciones de barberos', to: '/barber-applications', color: 'violet' },
      ] : [],
    },
  ];

  const gruposConLinks = navigationGroups.filter((group) => group.links.length > 0);
  const grupoActivo = gruposConLinks.find((group) =>
    group.links.some((link) => link.to === location.pathname)
  )?.value;

  useEffect(() => {
    if (!grupoActivo) {
      return;
    }

    setGruposAbiertos((prev) => (prev.includes(grupoActivo) ? prev : [...prev, grupoActivo]));
  }, [grupoActivo]);

  useEffect(() => {
    if (isMobile && opened) {
      toggle();
    }
  }, [location.pathname]);

  return (
    <AppShell
      header={{ height: isMobile ? 62 : 70 }}
      navbar={{
        width: isMobile ? 308 : 320,
        breakpoint: 'sm',
        collapsed: { mobile: !opened }
      }}
      padding={isMobile ? 'md' : 'lg'}
      styles={{
        main: {
          background: 'transparent',
          overflowX: 'clip',
        },
      }}
      className="app-shell-main"
    >
      <AppShell.Header style={{
        borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
        backdropFilter: 'blur(22px)',
        background: 'linear-gradient(180deg, rgba(248,250,252,0.92), rgba(248,250,252,0.78))',
        boxShadow: '0 8px 30px rgba(15, 23, 42, 0.04)',
      }}>
        <Group h="100%" px={isMobile ? 'sm' : 'md'} justify="space-between" wrap="nowrap">
          <Group gap="sm">
            <Burger opened={opened} onClick={toggle} aria-label="Abrir o cerrar navegación" hiddenFrom="sm" size="sm" />
            <Stack gap={0}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.08em' }}>
                Club de la Barba
              </Text>
              <Text size={isMobile ? 'sm' : 'lg'} fw={800} c="dark.8" lineClamp={1}>
                Giftcards y Códigos Club
              </Text>
            </Stack>
          </Group>
          <Group style={{ flexShrink: 1, minWidth: 0 }}>
            <Box
              sx={(theme) => ({
                padding: isMobile ? `${theme.spacing.xs} ${theme.spacing.xs}` : `${theme.spacing.xs} ${theme.spacing.sm}`,
                borderRadius: theme.radius.xl,
                background: 'linear-gradient(180deg, rgba(255,255,255,0.86), rgba(248,250,252,0.76))',
                border: '1px solid rgba(148, 163, 184, 0.14)',
                boxShadow: '0 12px 26px rgba(15, 23, 42, 0.05)',
                maxWidth: isMobile ? rem(150) : rem(260),
              })}
            >
              <Text size="xs" c="dimmed" fw={500} lineClamp={1}>{currentUser?.email}</Text>
            </Box>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar
        p={isMobile ? 'sm' : 'md'}
        className="mobile-safe-bottom"
        style={{
          borderRight: '1px solid rgba(148, 163, 184, 0.16)',
          backdropFilter: 'blur(20px)',
          background: 'linear-gradient(180deg, rgba(248,250,252,0.86), rgba(244,247,251,0.76))',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={(theme) => ({
            padding: isMobile ? theme.spacing.xs : theme.spacing.sm,
            borderRadius: theme.radius.xl,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.84), rgba(248,250,252,0.74))',
            border: '1px solid rgba(226, 232, 240, 0.95)',
            boxShadow: theme.shadows.xs,
          })}
        >
          <Stack spacing="xs">
            <Text size="xs" c="dimmed" tt="uppercase" fw={700} px="sm" pt="xs" style={{ letterSpacing: '0.08em' }}>
              Navegación
            </Text>
            <MainLink icon={IconHome} color="indigo" label="Inicio" to="/" active={location.pathname === "/"} />
            <MainLink icon={IconCalculator} color="indigo" label="Calculadora" to="/calculadora" active={location.pathname === "/calculadora"} />
            <Accordion
              multiple
              value={gruposAbiertos}
              onChange={setGruposAbiertos}
              variant="contained"
              chevronPosition="right"
              styles={(theme) => ({
                root: {
                  backgroundColor: 'transparent',
                },
                item: {
                  backgroundColor: 'transparent',
                  border: 'none',
                },
                control: {
                  padding: isMobile ? `${theme.spacing.sm} ${theme.spacing.sm}` : `${theme.spacing.xs} ${theme.spacing.sm}`,
                  borderRadius: theme.radius.lg,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: rgba(theme.colors.gray[0], 0.92),
                  },
                },
                label: {
                  padding: 0,
                },
                panel: {
                  padding: `${theme.spacing.xs} 0 ${theme.spacing.sm} ${theme.spacing.md}`,
                },
                chevron: {
                  color: theme.colors.gray[6],
                },
                content: {
                  padding: 0,
                },
              })}
            >
              {gruposConLinks.map((group) => {
                const grupoTieneActivo = group.value === grupoActivo;

                return (
                  <Accordion.Item key={group.value} value={group.value}>
                    <Accordion.Control
                      sx={(theme) => ({
                        border: grupoTieneActivo
                          ? `1px solid ${rgba(theme.colors[group.color][5], 0.22)}`
                          : '1px solid transparent',
                        backgroundColor: grupoTieneActivo
                          ? rgba(theme.colors[group.color][1], 0.65)
                          : 'transparent',
                      })}
                    >
                      <NavigationGroupControl
                        icon={group.icon}
                        color={group.color}
                        label={group.label}
                        active={grupoTieneActivo}
                      />
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap={6}>
                        {group.links.map((link) => (
                          <MainLink
                            key={link.label}
                            {...link}
                            nested
                            active={location.pathname === link.to}
                          />
                        ))}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Stack>
        </Box>

        <Stack
          spacing="xs"
          mt="md"
          sx={(theme) => ({
            padding: isMobile ? theme.spacing.xs : theme.spacing.sm,
            borderRadius: theme.radius.xl,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.86), rgba(248,250,252,0.78))',
            border: '1px solid rgba(226, 232, 240, 0.95)',
            position: 'sticky',
            bottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : 0,
            zIndex: 2,
            boxShadow: '0 14px 34px rgba(15, 23, 42, 0.06)',
          })}
        >
          <Button
            leftSection={<IconLogout size={rem(12)} style={{ width: '1rem', height: '1rem' }} />}
            onClick={handleLogout}
            color="dark"
            variant="default"
            fullWidth
            size="sm"
            styles={(theme) => ({
              root: {
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                fontSize: '0.85rem',
                height: '42px',
                '&:hover': {
                  backgroundColor: theme.colors.gray[1],
                },
              },
            })}
          >
            Cerrar Sesión
          </Button>
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        <Box
          sx={(theme) => ({
            minHeight: isMobile ? 'calc(100dvh - 78px)' : 'calc(100dvh - 110px)',
            padding: isMobile ? theme.spacing.xs : theme.spacing.sm,
            borderRadius: theme.radius.xl,
          })}
        >
          <Outlet />
        </Box>
      </AppShell.Main>

    </AppShell>
  );
};

export default Layout; 
